import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://stocktrader.applytracker.workers.dev';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true
    });
    return instance;
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      setToken(null);
      setLoading(false);
      return false;
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const data = response.data;

    if (data.requires_2fa) {
      setTempToken(data.access_token);
      setRequires2FA(true);
      return { requires2FA: true };
    }

    setToken(data.access_token);
    setUser({
      id: data.user_id,
      email: data.email,
      name: data.name,
      two_fa_enabled: data.two_fa_enabled,
    });
    setRequires2FA(false);
    return { requires2FA: false };
  };

  const verify2FA = async (code) => {
    const response = await axios.post(
      `${API_URL}/api/auth/2fa/verify`,
      { code },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tempToken}`,
        },
        withCredentials: true
      }
    );
    const data = response.data;

    setToken(data.access_token);
    setTempToken(null);
    setRequires2FA(false);
    setUser({
      id: data.user_id,
      email: data.email,
      name: data.name,
      two_fa_enabled: data.two_fa_enabled,
    });
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    const data = response.data;

    setToken(data.access_token);
    setUser({
      id: data.user_id,
      email: data.email,
      name: data.name,
      two_fa_enabled: false,
    });
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setToken(null);
    setUser(null);
    setRequires2FA(false);
    setTempToken(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = useMemo(() => ({
    user,
    token,
    loading,
    requires2FA,
    isAuthenticated: !!token && !!user,
    login,
    verify2FA,
    register,
    logout,
    refreshUser,
    api,
  }), [user, token, loading, requires2FA, api]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
